import { Stack } from '@mui/material';
import { MHMTextField } from '../MHM/MHMTextField';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMButton } from '../MHM/MHMButton';
import { type FormEvent, useState } from 'react';
import { account_controller } from '../../../controllers/account_controller';
import { ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { ToggleForgotPasswordForm } from './ToggleForgotPasswordForm';

type Props = {
  onReturnToLogin(): void;
};

export function ForgotPasswordForm({ onReturnToLogin }: Props) {
  const { t } = useTranslation();
  const [forgotEmailValidationError, setForgotEmailValidationError] =
    useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email')?.toString() ?? '';
    if (
      !email ||
      email.length < 5 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      setForgotEmailValidationError('Please provide a valid email address!');
    } else {
      setForgotEmailValidationError('');
      setIsSendingEmail(true);
      account_controller.send_password_link(email, () => {
        setIsSendingEmail(false);
        setEmailSent(true);
      });
    }
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      justifyContent="center"
      alignItems="center"
      gap={2}
      width="100%"
    >
      <MHMTextField
        name="email"
        placeholder={t('email')}
        fullWidth
        slotProps={{ input: { sx: { bgcolor: 'background.paper' } } }}
        error={Boolean(forgotEmailValidationError)}
        helperText={
          <ErrorText component="span">
            {t(forgotEmailValidationError)}
          </ErrorText>
        }
      />

      <ToggleForgotPasswordForm onClick={onReturnToLogin}>
        {t('Return to Login')}
      </ToggleForgotPasswordForm>

      <MHMButton
        sx={{ width: 'fit-content' }}
        type="submit"
        size="large"
        disabled={isSendingEmail}
      >
        {t('Send me an email to reset password')}
      </MHMButton>
      {emailSent ? (
        <MHMTypography variant="h6" component="p" textAlign="center">
          {t(
            'If an account exists for this email, you will receive an email with instructions to reset your password. This can take up to 5 minutes and may arrive in your junk folder.',
          )}
        </MHMTypography>
      ) : null}
    </Stack>
  );
}
